/**
 * FieldComparisonDialog — per-field accept/reject review UI for demographic
 * fields extracted from an ID (name / DOB / address / gender) vs the current
 * CIF values on the form.
 *
 * Per design doc §13a (Individual CIP scan):
 *   - CIP fields (idTypeCode/idNumber/idIssuer/idIssueDate/idExpiryDate) are
 *     NOT shown here — IdCapturePanel writes those immediately via onChange
 *     when extraction returns.
 *   - Only demographic fields show up: name (reviewed as one row), DOB,
 *     address (street/city/state/zip), gender.
 *   - Each row: status badge + current CIF value + scanned value + Accept
 *     button (only on "Differs" rows).
 *   - Name rows are atomic — accepting patches first/middle/last together.
 *
 * Ported from legacy SPFx `IdCapturePanel.tsx` lines 419-464 (comparison
 * builder) + lines 716-783 (render) into a standalone component.
 *
 * Directional ambiguity (2026-04-24): when the AI returned a city that
 * begins with a directional word (e.g. "Northwest Minneapolis" where
 * "Northwest" may actually be the street's post-directional), we surface
 * an inline disambiguation banner on the Address/City rows. Officer picks
 * which interpretation persists; the row values + patch re-build against
 * the chosen variant. A recommended choice is blue-highlighted based on
 * a small allowlist of known directionally-named cities
 * (see postProcessAddress.ts KNOWN_DIRECTIONAL_CITIES).
 */

import { useMemo, useState, type CSSProperties, type JSX } from 'react';
import type { RaoPerson } from '../../types.js';
import type { ExtractedIdFields } from '../../services/IdExtractionService.js';
import type {
  DirectionalAmbiguity,
  StreetSuffixAmbiguity,
} from '../../utils/postProcessAddress.js';
import { titleCase } from '../../utils/titleCase.js';

type FieldStatus = 'match' | 'differs' | 'blank';

interface FieldRow {
  key: string;
  label: string;
  status: FieldStatus;
  cifValue: string;
  scannedValue: string;
  /** Patch to apply on Accept. */
  patch: Partial<RaoPerson>;
}

export interface FieldComparisonDialogProps {
  person: RaoPerson;
  extracted: ExtractedIdFields;
  onAccept: (patch: Partial<RaoPerson>) => void;
}

// ---------------------------------------------------------------------------
// Normalization helpers (scoped to comparison — won't leak)
// ---------------------------------------------------------------------------

function norm(s: string | undefined | null): string {
  return (s ?? '')
    .toLowerCase()
    .replace(/[.,#-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const ADDR_ABBR: Record<string, string> = {
  ln: 'lane', dr: 'drive', st: 'street', ave: 'avenue', blvd: 'boulevard',
  ct: 'court', cir: 'circle', pl: 'place', rd: 'road', hwy: 'highway',
  pkwy: 'parkway', trl: 'trail', way: 'way', ter: 'terrace', sq: 'square',
  n: 'north', s: 'south', e: 'east', w: 'west',
  ne: 'northeast', nw: 'northwest', se: 'southeast', sw: 'southwest',
  apt: 'apartment', ste: 'suite', fl: 'floor', bldg: 'building',
};

function expandAbbr(s: string): string {
  return s.split(' ').map((w) => ADDR_ABBR[w] ?? w).join(' ');
}

function addrMatches(a: string | undefined, b: string | undefined): boolean {
  return expandAbbr(norm(a)) === expandAbbr(norm(b));
}

// ---------------------------------------------------------------------------
// Build rows
// ---------------------------------------------------------------------------

/**
 * Override the scanned addr/city values based on the officer's disambiguation
 * choice. When 'keep-as-is', use the AI-returned city as-is (e.g. "Northwest
 * Minneapolis"). When 'move-to-street', append the directional to the street
 * and strip it from the city.
 */
function applyDirectionalChoice(
  extracted: ExtractedIdFields,
  ambiguity: DirectionalAmbiguity,
  choice: 'keep-as-is' | 'move-to-street',
): { addr: string; city: string } {
  if (choice === 'keep-as-is') {
    return {
      addr: extracted.addr,
      city: titleCase(ambiguity.cityAsIs),
    };
  }
  return {
    addr: ambiguity.alternateAddress,
    city: titleCase(ambiguity.alternateCity),
  };
}

/**
 * Override the scanned addr/city values based on the officer's street-suffix
 * disambiguation choice. When 'keep-as-is', use the AI-returned city as-is
 * (e.g. "St Joseph"). When 'move-to-street', append the suffix back onto
 * the street line and strip it from the city.
 */
function applyStreetSuffixChoice(
  extracted: ExtractedIdFields,
  ambiguity: StreetSuffixAmbiguity,
  choice: 'keep-as-is' | 'move-to-street',
): { addr: string; city: string } {
  if (choice === 'keep-as-is') {
    return {
      addr: extracted.addr,
      city: titleCase(ambiguity.cityAsIs),
    };
  }
  return {
    addr: ambiguity.alternateAddress,
    city: titleCase(ambiguity.alternateCity),
  };
}

function buildRows(
  person: RaoPerson,
  extracted: ExtractedIdFields,
  directionalChoice: 'keep-as-is' | 'move-to-street' | null,
  streetSuffixChoice: 'keep-as-is' | 'move-to-street' | null,
): FieldRow[] {
  const rows: FieldRow[] = [];

  // Name — reviewed as one atomic row.
  const scannedNameParts = [extracted.firstName, extracted.middleName, extracted.lastName]
    .map((p) => (p ?? '').trim())
    .filter(Boolean);
  const scannedName = scannedNameParts.join(' ');
  const cifNameParts = [person.firstName, person.middleName, person.lastName]
    .map((p) => (p ?? '').trim())
    .filter(Boolean);
  const cifName = cifNameParts.join(' ');
  if (scannedName) {
    const namePatch: Partial<RaoPerson> = {};
    if (extracted.firstName) namePatch.firstName = titleCase(extracted.firstName);
    if (extracted.middleName) namePatch.middleName = titleCase(extracted.middleName);
    if (extracted.lastName) namePatch.lastName = titleCase(extracted.lastName);

    if (!cifName) {
      rows.push({
        key: 'name',
        label: 'Name',
        status: 'blank',
        cifValue: '',
        scannedValue: scannedName,
        patch: namePatch,
      });
    } else if (norm(cifName) === norm(scannedName)) {
      rows.push({
        key: 'name',
        label: 'Name',
        status: 'match',
        cifValue: cifName,
        scannedValue: scannedName,
        patch: {},
      });
    } else {
      rows.push({
        key: 'name',
        label: 'Name',
        status: 'differs',
        cifValue: cifName,
        scannedValue: scannedName,
        patch: namePatch,
      });
    }
  }

  // Apply disambiguation choice (directional OR street-suffix; mutually
  // exclusive per postProcessAddress contract) to the values we compare/patch
  // for addr + city.
  let scannedAddr = extracted.addr;
  let scannedCity = extracted.city;
  if (extracted.directionalAmbiguity && directionalChoice) {
    const adjusted = applyDirectionalChoice(
      extracted,
      extracted.directionalAmbiguity,
      directionalChoice,
    );
    scannedAddr = adjusted.addr;
    scannedCity = adjusted.city;
  } else if (extracted.streetSuffixAmbiguity && streetSuffixChoice) {
    const adjusted = applyStreetSuffixChoice(
      extracted,
      extracted.streetSuffixAmbiguity,
      streetSuffixChoice,
    );
    scannedAddr = adjusted.addr;
    scannedCity = adjusted.city;
  } else if (
    (extracted.directionalAmbiguity || extracted.streetSuffixAmbiguity) &&
    !directionalChoice && !streetSuffixChoice
  ) {
    // Ambiguity flagged but no choice yet — reflect whatever is in extracted
    // (post-service titleCase). Officer must click before "Accept" activates.
    scannedAddr = extracted.addr;
    scannedCity = titleCase(extracted.city);
  } else {
    // No ambiguity — still run titleCase on city defensively in case an
    // upstream lowercase snuck through (observed in the Minneapolis
    // sample that motivated this feature).
    scannedCity = titleCase(extracted.city);
  }

  // Single-field comparisons.
  const single = (
    key: keyof RaoPerson,
    label: string,
    cifVal: string | undefined,
    scanned: string | undefined,
    matcher: (a: string | undefined, b: string | undefined) => boolean = (a, b) => norm(a) === norm(b),
  ): void => {
    const s = (scanned ?? '').trim();
    if (!s) return;
    const cif = (cifVal ?? '').trim();
    const patch = { [key]: s } as unknown as Partial<RaoPerson>;
    if (!cif) {
      rows.push({ key, label, status: 'blank', cifValue: '', scannedValue: s, patch });
    } else if (matcher(cif, s)) {
      rows.push({ key, label, status: 'match', cifValue: cif, scannedValue: s, patch: {} });
    } else {
      rows.push({ key, label, status: 'differs', cifValue: cif, scannedValue: s, patch });
    }
  };

  single('dob', 'Date of Birth', person.dob, extracted.dob);
  single('addr', 'Address', person.addr, scannedAddr, (a, b) => norm(a) === norm(b) || addrMatches(a, b));
  single('city', 'City', person.city, scannedCity);
  single('state', 'State', person.state, extracted.state);
  single('zip', 'ZIP', person.zip, extracted.zip);
  single('gender', 'Gender', person.gender, extracted.gender);

  return rows;
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

const STATUS_VISUALS: Record<FieldStatus, { label: string; icon: string; color: string; bg: string }> = {
  match:   { label: 'Match',       icon: '✓', color: 'var(--ok-text)', bg: 'var(--ok-bg)' },
  blank:   { label: 'Auto-filled', icon: '✓', color: 'var(--pcb-blue)', bg: 'var(--info-bg)' },
  differs: { label: 'Differs',     icon: '⚠', color: 'var(--warn-text)', bg: 'var(--warn-bg)' },
};

export function FieldComparisonDialog({
  person,
  extracted,
  onAccept,
}: FieldComparisonDialogProps): JSX.Element | null {
  const ambiguity = extracted.directionalAmbiguity;
  const suffixAmbiguity = extracted.streetSuffixAmbiguity;

  // Default each choice to the service's recommendation so the row values
  // and the recommended button highlight agree from first render. Both
  // states are kept independent even though postProcessAddress makes the
  // two ambiguities mutually exclusive in practice.
  const [directionalChoice, setDirectionalChoice] = useState<
    'keep-as-is' | 'move-to-street' | null
  >(ambiguity ? ambiguity.recommendedChoice : null);
  const [streetSuffixChoice, setStreetSuffixChoice] = useState<
    'keep-as-is' | 'move-to-street' | null
  >(suffixAmbiguity ? suffixAmbiguity.recommendedChoice : null);

  const initialRows = useMemo(
    () => buildRows(person, extracted, directionalChoice, streetSuffixChoice),
    [person, extracted, directionalChoice, streetSuffixChoice],
  );
  const [rows, setRows] = useState<FieldRow[]>(initialRows);

  // If the extracted / person data changes (e.g. re-run) or the officer
  // flips the disambiguation choice, refresh the rows.
  useMemo(() => setRows(initialRows), [initialRows]);

  if (rows.length === 0) {
    return (
      <div
        style={{
          marginTop: 10,
          padding: 12,
          borderRadius: 8,
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          fontSize: 12,
          color: 'var(--ink-600)',
          fontFamily: "'Segoe UI', Roboto, sans-serif",
        }}
      >
        No demographic fields returned from the scan.
      </div>
    );
  }

  const acceptRow = (idx: number): void => {
    const row = rows[idx];
    if (!row) return;
    onAccept(row.patch);
    const updated = rows.slice();
    updated[idx] = { ...row, status: 'match', cifValue: row.scannedValue };
    setRows(updated);
  };

  return (
    <div
      style={{
        marginTop: 10,
        padding: '10px 12px',
        background: 'var(--surface-card)',
        borderRadius: 8,
        border: '1px solid var(--surface-border)',
        fontFamily: "'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: 'var(--pcb-blue-dark)',
          marginBottom: 6,
        }}
      >
        ID vs CIF comparison
      </div>

      {ambiguity && (
        <DirectionalAmbiguityBanner
          ambiguity={ambiguity}
          extracted={extracted}
          selected={directionalChoice}
          onSelect={setDirectionalChoice}
        />
      )}

      {suffixAmbiguity && !ambiguity && (
        <StreetSuffixAmbiguityBanner
          ambiguity={suffixAmbiguity}
          extracted={extracted}
          selected={streetSuffixChoice}
          onSelect={setStreetSuffixChoice}
        />
      )}

      {rows.map((r, i) => {
        const v = STATUS_VISUALS[r.status];
        return (
          <div
            key={r.key}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '6px 0',
              borderBottom: i < rows.length - 1 ? '1px solid var(--surface-border)' : 'none',
            }}
          >
            <span
              style={{
                fontSize: 12,
                padding: '1px 6px',
                borderRadius: 10,
                background: v.bg,
                color: v.color,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                minWidth: 80,
                textAlign: 'center',
                marginTop: 1,
              }}
            >
              {v.icon} {v.label}
            </span>
            <span style={{ fontSize: 12, color: 'var(--ink-600)', minWidth: 90, fontWeight: 500 }}>
              {r.label}
            </span>
            <div style={{ flex: 1, fontSize: 12 }}>
              {r.status === 'match' && <span style={{ color: 'var(--ink-700)' }}>{r.scannedValue}</span>}
              {r.status === 'blank' && <span style={{ color: 'var(--pcb-blue)' }}>{r.scannedValue}</span>}
              {r.status === 'differs' && (
                <div>
                  <div style={{ color: 'var(--ink-500)', fontSize: 12 }}>
                    <span style={{ textDecoration: 'line-through' }}>CIF: {r.cifValue}</span>
                  </div>
                  <div style={{ color: 'var(--ink-700)', fontWeight: 500 }}>ID: {r.scannedValue}</div>
                </div>
              )}
            </div>
            {r.status === 'differs' && (
              <button
                type="button"
                onClick={() => acceptRow(i)}
                style={{
                  padding: '2px 10px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: 'none',
                  background: 'var(--brand-fill)',
                  color: 'var(--on-brand)',
                  whiteSpace: 'nowrap',
                  fontFamily: "'Segoe UI', Roboto, sans-serif",
                }}
              >
                Accept
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Directional ambiguity banner
// ---------------------------------------------------------------------------

interface DirectionalAmbiguityBannerProps {
  ambiguity: DirectionalAmbiguity;
  extracted: ExtractedIdFields;
  selected: 'keep-as-is' | 'move-to-street' | null;
  onSelect: (choice: 'keep-as-is' | 'move-to-street') => void;
}

function DirectionalAmbiguityBanner({
  ambiguity,
  extracted,
  selected,
  onSelect,
}: DirectionalAmbiguityBannerProps): JSX.Element {
  const cityAsIsDisplay = titleCase(ambiguity.cityAsIs);
  const alternateCityDisplay = titleCase(ambiguity.alternateCity);
  const alternateAddrDisplay = titleCase(ambiguity.alternateAddress);
  const currentStreetDisplay = titleCase(extracted.addr);

  const recommended = ambiguity.recommendedChoice;

  const choiceButtonStyle = (
    choice: 'keep-as-is' | 'move-to-street',
  ): CSSProperties => {
    const isSelected = selected === choice;
    const isRecommended = recommended === choice;
    // Recommended gets a solid blue highlight; the other option is a plain
    // outlined button. When the officer selects the non-recommended option
    // we still inflect the border so the selection is visible.
    const background = isSelected
      ? (isRecommended ? 'var(--brand-fill)' : 'var(--brand-fill-strong)')
      : (isRecommended ? 'var(--info-bg)' : 'var(--surface-card)');
    const color = isSelected ? 'var(--on-brand)' : (isRecommended ? 'var(--pcb-blue-dark)' : 'var(--ink-800)');
    const border = isRecommended
      ? '1px solid var(--pcb-blue)'
      : (isSelected ? '1px solid var(--pcb-blue-dark)' : '1px solid var(--surface-border)');
    return {
      padding: '6px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      background,
      color,
      border,
      whiteSpace: 'normal',
      textAlign: 'left',
      lineHeight: 1.35,
      fontFamily: "'Segoe UI', Roboto, sans-serif",
      flex: 1,
      minWidth: 0,
    };
  };

  return (
    <div
      style={{
        marginBottom: 10,
        padding: '10px 12px',
        borderRadius: 8,
        background: 'var(--warn-bg)',
        border: '1px solid var(--warn-line)',
        fontSize: 12,
        color: 'var(--warn-text)',
        fontFamily: "'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        {'⚠'} "{ambiguity.leadingWord}" could be a street directional or
        part of the city name.
      </div>
      <div style={{ marginBottom: 8, color: 'var(--warn-text)' }}>
        {recommended === 'keep-as-is'
          ? `"${cityAsIsDisplay}" matches a known U.S. city name — we recommend keeping it as-is.`
          : `"${cityAsIsDisplay}" is not in our known-directional-city list — we recommend moving the directional onto the street line.`}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => onSelect('keep-as-is')}
          style={choiceButtonStyle('keep-as-is')}
          aria-pressed={selected === 'keep-as-is' ? 'true' : 'false'}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>
            Keep as-is
            {recommended === 'keep-as-is' && (
              <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.85 }}>
                (recommended)
              </span>
            )}
          </div>
          <div style={{ fontWeight: 400 }}>
            Street: {currentStreetDisplay || '(empty)'}
            <br />
            City: {cityAsIsDisplay}
          </div>
        </button>
        <button
          type="button"
          onClick={() => onSelect('move-to-street')}
          style={choiceButtonStyle('move-to-street')}
          aria-pressed={selected === 'move-to-street' ? 'true' : 'false'}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>
            Move directional to street
            {recommended === 'move-to-street' && (
              <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.85 }}>
                (recommended)
              </span>
            )}
          </div>
          <div style={{ fontWeight: 400 }}>
            Street: {alternateAddrDisplay}
            <br />
            City: {alternateCityDisplay}
          </div>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Street-suffix ambiguity banner (mirror of directional)
// ---------------------------------------------------------------------------

interface StreetSuffixAmbiguityBannerProps {
  ambiguity: StreetSuffixAmbiguity;
  extracted: ExtractedIdFields;
  selected: 'keep-as-is' | 'move-to-street' | null;
  onSelect: (choice: 'keep-as-is' | 'move-to-street') => void;
}

function StreetSuffixAmbiguityBanner({
  ambiguity,
  extracted,
  selected,
  onSelect,
}: StreetSuffixAmbiguityBannerProps): JSX.Element {
  const cityAsIsDisplay = titleCase(ambiguity.cityAsIs);
  const alternateCityDisplay = titleCase(ambiguity.alternateCity);
  const alternateAddrDisplay = titleCase(ambiguity.alternateAddress);
  const currentStreetDisplay = titleCase(extracted.addr);

  const recommended = ambiguity.recommendedChoice;
  const smushedNote = ambiguity.smushed
    ? ' The AI returned the city and suffix glued together (no space) — almost always a parser glitch.'
    : '';

  const choiceButtonStyle = (
    choice: 'keep-as-is' | 'move-to-street',
  ): CSSProperties => {
    const isSelected = selected === choice;
    const isRecommended = recommended === choice;
    const background = isSelected
      ? (isRecommended ? 'var(--brand-fill)' : 'var(--brand-fill-strong)')
      : (isRecommended ? 'var(--info-bg)' : 'var(--surface-card)');
    const color = isSelected ? 'var(--on-brand)' : (isRecommended ? 'var(--pcb-blue-dark)' : 'var(--ink-800)');
    const border = isRecommended
      ? '1px solid var(--pcb-blue)'
      : (isSelected ? '1px solid var(--pcb-blue-dark)' : '1px solid var(--surface-border)');
    return {
      padding: '6px 10px',
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      background,
      color,
      border,
      whiteSpace: 'normal',
      textAlign: 'left',
      lineHeight: 1.35,
      fontFamily: "'Segoe UI', Roboto, sans-serif",
      flex: 1,
      minWidth: 0,
    };
  };

  return (
    <div
      style={{
        marginBottom: 10,
        padding: '10px 12px',
        borderRadius: 8,
        background: 'var(--warn-bg)',
        border: '1px solid var(--warn-line)',
        fontSize: 12,
        color: 'var(--warn-text)',
        fontFamily: "'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        {'⚠'} "{ambiguity.leadingWord}" looks like a street suffix that may
        belong on the street line, not the city.
      </div>
      <div style={{ marginBottom: 8, color: 'var(--warn-text)' }}>
        {recommended === 'keep-as-is'
          ? `"${cityAsIsDisplay}" matches a known "Saint" / legitimate-prefix city — we recommend keeping it as-is.${smushedNote}`
          : `"${cityAsIsDisplay}" is not a recognized city; the suffix likely bled in from the prior line. We recommend moving it back onto the street.${smushedNote}`}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => onSelect('keep-as-is')}
          style={choiceButtonStyle('keep-as-is')}
          aria-pressed={selected === 'keep-as-is' ? 'true' : 'false'}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>
            Keep as-is
            {recommended === 'keep-as-is' && (
              <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.85 }}>
                (recommended)
              </span>
            )}
          </div>
          <div style={{ fontWeight: 400 }}>
            Street: {currentStreetDisplay || '(empty)'}
            <br />
            City: {cityAsIsDisplay}
          </div>
        </button>
        <button
          type="button"
          onClick={() => onSelect('move-to-street')}
          style={choiceButtonStyle('move-to-street')}
          aria-pressed={selected === 'move-to-street' ? 'true' : 'false'}
        >
          <div style={{ fontWeight: 700, marginBottom: 2 }}>
            Move suffix to street
            {recommended === 'move-to-street' && (
              <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.85 }}>
                (recommended)
              </span>
            )}
          </div>
          <div style={{ fontWeight: 400 }}>
            Street: {alternateAddrDisplay}
            <br />
            City: {alternateCityDisplay}
          </div>
        </button>
      </div>
    </div>
  );
}
